import { useState, useEffect, useRef, useCallback } from 'react';
import kuickLogo from '../../assets/kuick_logo.svg';
import DineOrderLogo from '../../assets/images/default_images/dineorder-logo.png';
import crossIcon from '../../assets/images/CrossIcon.png';
import OtpInput from '../Elements/OtpInput';
import Button from '../Elements/Button';
import CountryCodeDropdown from '../Elements/CountryCodeDropdown';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Edit } from '../Icon/Edit';
import { VerifyButton } from '../Icon/VerifyButton';
import { RightArrow } from '../Icon/RightArrow';
import {
	isNumeric,
	removeAllSpaceFromString,
	setSession,
	getTimeRemaining,
	setSessionUserId,
	setSessionCCId,
	removeDismissedPendingOrders,
	eWebsiteTypes
} from '../../utils';
import { loginCall, getBasicDineData } from '../../services';
import { error } from '../notify';
import { setUserData, setUserAddress, setLoginModule } from '../../store/userData/userDataSlice';
import { setBasicDineData } from '../../store/basicData/basicDataSlice';
import Modal from 'react-modal';
import useWindowDimensions from '../../hooks/useWindowsDimensons';
import { usePageLoader } from '../../hooks/usePageLoader';
import { HomePage, LoginPage, RegistrationPage } from '../../routes/RoutesPath';
import CaptchaBox from '../capchaBox/CapchaBox';
import { OTP_LENGTH } from '@/constants/configurations';
import Spinner from '../Elements/Spinner';

const LoginModal = ({ visible, onClose, showCloseIcon = true }) => {
	const navigation = useNavigate();
	const dispatch = useDispatch();

	const [otpLength, setOtpLength] = useState(OTP_LENGTH);

	const settings = useSelector(state => state?.basicdata?.settings);
	const websiteColor = useSelector(state => state?.onLoadData?.color);
	const websiteType = useSelector(state => state?.onLoadData?.websiteType);

	const [loader, setLoader] = useState(false);
	const [mobileNumber, setMobileNumber] = useState('');
	const [countryCode, setCountryCode] = useState(settings?.countryCode ? settings.countryCode[0] : '+44');
	const [step, setStep] = useState(1);
	const [ctoken, setCToken] = useState(null);
	const [showResend, setShowResend] = useState(false);
	const [otp, setOtp] = useState('');
	const [firstName, setFirstName] = useState('');
	const [referalCode, setReferalCode] = useState('');
	const [timer, setTimer] = useState('00:00');
	const [isLoginPage, setIsLoginPage] = useState(true);
	const [pageNames, setpageName] = useState('');
	const [isParentToggle, setIsParentToggle] = useState(false);
	const [isSmall, setIsSmall] = useState(false);

	// Timer-related
	const [deadline, setDeadline] = useState(null);

	const timerRef = useRef(null);
	const dropDownRef = useRef(null);
	const location = useLocation();
	const { width } = useWindowDimensions();
	const recaptchaScale = Math.min(width / 480, 1);

	usePageLoader(loader);

	// ----------------- Helper: reset all local state -----------------
	const resetState = () => {
		setStep(1);
		setMobileNumber('');
		setOtp('');
		setFirstName('');
		setReferalCode('');
		setTimer('00:00');
		setCToken(null);
		setShowResend(false);
		setIsLoginPage(true);
		setIsParentToggle(false);
		setLoader(false);
		setDeadline(null);
		if (timerRef.current) clearInterval(timerRef.current);
	};

	// ----------------- Timer helpers -----------------
	const createDeadline = attempt => {
		const d = new Date();
		const extraSeconds = 30 + Math.max(attempt, 0) * 30;
		d.setSeconds(d.getSeconds() + extraSeconds);
		return d;
	};

	const startTimer = endTime => {
		const { total, minutes, seconds } = getTimeRemaining(endTime);

		if (total >= 0) {
			if (total === 0) {
				setShowResend(true);
				if (timerRef.current) clearInterval(timerRef.current);
			}

			setTimer((minutes > 9 ? minutes : '0' + minutes) + ':' + (seconds > 9 ? seconds : '0' + seconds));
		}
	};

	const clearAndRunTimer = endTime => {
		if (!endTime) return;

		const { minutes, seconds } = getTimeRemaining(endTime);
		setTimer((minutes > 9 ? minutes : '0' + minutes) + ':' + (seconds > 9 ? seconds : '0' + seconds));

		if (timerRef.current) clearInterval(timerRef.current);
		const id = setInterval(() => {
			startTimer(endTime);
		}, 1000);
		timerRef.current = id;
	};

	// ----------------- Effects -----------------

	useEffect(() => {
		setIsSmall(width < 640);
	}, [width]);

	useEffect(() => {
		if (!deadline) {
			if (timerRef.current) clearInterval(timerRef.current);
			setTimer('00:00');
			setShowResend(false);
			return;
		}

		setShowResend(false);
		clearAndRunTimer(deadline);

		return () => {
			if (timerRef.current) clearInterval(timerRef.current);
		};
	}, [deadline]);

	useEffect(() => {
		if (visible) {
			resetState();
		} else {
			if (timerRef.current) clearInterval(timerRef.current);
		}
	}, [visible]);

	useEffect(() => {
		const handleClickOutside = event => {
			if (dropDownRef.current && !dropDownRef.current.contains(event.target)) {
				setIsParentToggle(false);
			}
		};

		document.addEventListener('click', handleClickOutside, true);
		return () => {
			document.removeEventListener('click', handleClickOutside, true);
		};
	}, []);

	useEffect(() => {
		return () => {
			if (timerRef.current) clearInterval(timerRef.current);
		};
	}, []);

	// ----------------- Handlers -----------------

	const handleToggle = () => {
		if (step === 2 || step === 3) {
			setStep(1);
			setDeadline(null);
			setTimer('00:00');
			if (timerRef.current) clearInterval(timerRef.current);
		}
		setIsLoginPage(prev => !prev);
	};

	const onCloseButton = () => {
		resetState();
		onClose();
		dispatch(setLoginModule(false));
	};

	const resetCaptchaToken = () => {
		setCToken(null);
	};

	const handleCaptchaToken = useCallback(token => {
		setCToken(token);
	}, []);

	const [otpResendCounter, setOtpResendCounter] = useState(0);

	const getLogin = async (pageName, resend = 'No', mode = 'SMS') => {
		setpageName(() => pageName);

		if (mobileNumber?.length === 11 && mobileNumber?.charAt(0) !== '0') {
			error('Invalid mobile number. Please enter a valid number', true);
			return;
		}

		setLoader(true);

		try {
			const res = await loginCall(
				countryCode,
				mobileNumber,
				otp,
				resend,
				mode,
				pageName,
				firstName,
				referalCode,
				ctoken // ✅ same as Angular's gtoken
			);

			res.data.otpLen && setOtpLength(res.data.otpLen);

			if (resend.toLocaleLowerCase() == 'yes' || pageName.toLocaleLowerCase() == 'reotp') {
				setDeadline(createDeadline(otpResendCounter));
				setOtpResendCounter(prev => prev + 1);
			} else {
				setDeadline(createDeadline(0));
				setOtpResendCounter(prev => prev + 1);
			}

			if (res.status === 200) {
				const { status, msg } = res.data;

				if (status === 'success') {
					// if (bk) {
					// 	if (pageName !== 'reotp') {
					// 		error('Something went wrong!! Please try again', true);
					// 	} else {
					// 		setOtp('');
					// 		setShowResend(false);
					// 		setStep(2);
					// 		resetCaptchaToken();
					// 	}
					// } else {
						if (step === 1) {
							setStep(2);
							resetCaptchaToken();
							setShowResend(false);
						} else if (step === 2) {
							const { customer } = res.data;

							if (customer?.common_customer_First_Name === '') {
								setStep(3);
								setDeadline(null);
							} else {
								document.body.style.overflow = '';
								document.body.style.height = 'auto';

								setSessionUserId(res?.data?.config?.usrId);
								setSessionCCId(res?.data?.config?.ccid);

								if (res?.data?.customer?.common_customer_Address?.length) {
									dispatch(
										setUserAddress({
											selected: res?.data?.customer?.common_customer_Address[0],
											address: res?.data?.customer?.common_customer_Address
										})
									);
								}

								dispatch(setUserData(res?.data));
								setSession(customer);
								removeDismissedPendingOrders();

								dispatch(setLoginModule(false));
								getBasicDineData().then(resBasic => {
									if (resBasic.status === 200) {
										dispatch(setBasicDineData(resBasic.data));
									}
								});

								const route = location.pathname;
								if (route === LoginPage || route === RegistrationPage) {
									navigation(HomePage);
								}

								setDeadline(null);
							}
						} else if (step === 3) {
							document.body.style.overflow = '';
							document.body.style.height = 'auto';

							const { customer } = res.data;
							setSessionUserId(res?.data?.config?.usrId);
							setSessionCCId(res?.data?.config?.ccid);
							dispatch(setUserData(res?.data));
							setSession(customer);
							removeDismissedPendingOrders();
							dispatch(setLoginModule(false));

							const route = location.pathname;
							if (route === LoginPage || route === RegistrationPage) {
								navigation(HomePage);
							}

							setDeadline(null);
						}
					// }
				} else if (status === 'valid_error' || status === 'error') {
					error(msg, true);
					resetCaptchaToken();
				} else {
					error(msg, true);
					resetCaptchaToken();
				}
			} else {
				error('Something went wrong !! Please try again');
				resetCaptchaToken();
			}
		} catch (e) {
			console.error(e);
			error('Something went wrong !! Please try again', true);
			resetCaptchaToken();
		} finally {
			setLoader(false);
		}
	};

	const getHeader = () => {
		switch (step) {
			case 1:
				return 'Welcome';
			case 2:
				return 'One Time Password';
			default:
				return '';
		}
	};

	const getSubHeader = () => {
		switch (step) {
			case 1:
				return 'Enter Mobile Number';
			case 2:
				return (
					<span>
						We have sent the OTP verification to
						<span className="flex space-x-2 items-center">
							<span>{` ${countryCode} ${mobileNumber}`}</span>
							<Button
								onClick={() => {
									setStep(1);
									setDeadline(null);
									resetCaptchaToken();
									setShowResend(false);
									setTimer('00:00');
									if (timerRef.current) clearInterval(timerRef.current);
								}}
							>
								<Edit className="cursor-pointer" />
							</Button>
						</span>
					</span>
				);
			case 3:
				return <span className="font-semibold font-inter text-[20px] sm:text-[24px]">Update Your Details.</span>;
			default:
				return '';
		}
	};

	const getBody = () => {
		switch (step) {
			case 1:
				return (
					<div className="border border-gray-300 focus-within:border-blue-500 focus:outline-none w-full rounded-lg flex mt-3">
						<div className="flex items-center w-full">
							<div
								className="w-[22%] sm:w-[32%] pl-4 py-2"
								onClick={() => {
									setIsParentToggle(!isParentToggle);
								}}
								ref={dropDownRef}
							>
								<CountryCodeDropdown
									value={countryCode}
									className="sm:w-full"
									onChange={value => {
										setCountryCode(() => value);
									}}
									area="Login"
									parentToggle={isParentToggle}
								/>
							</div>
							<div className="w-[78%] sm:w-[68%] pl-8 xs:pl-4 sm:px-2 py-2">
								<input
									type="text"
									maxLength={11}
									inputMode="numeric"
									autoComplete="off"
									placeholder="XXXXXXXXXX"
									value={mobileNumber}
									className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none w-full md:h-10 text-sm sm:text-md font-normal font-inter leading-normal sm:w-full focus:outline-none"
									onChange={e => {
										const { value } = e.target;
										let typed = removeAllSpaceFromString(value);
										if (isNumeric(typed)) {
											setMobileNumber(typed);
										} else if (value === '') {
											setMobileNumber('');
										}
									}}
								/>
							</div>
						</div>
					</div>
				);
			case 2:
				return (
					<div className="pt-6 space-x-2">
						<OtpInput length={otpLength} onOtpSubmit={setOtp} pageNames={pageNames} />
					</div>
				);
			case 3:
				return (
					<div className="w-full rounded-lg">
						<div className="text-textPrimary text-base sm:text-mdl font-normal font-inter pt-4 sm:pt-6">
							Enter Your Full Name
						</div>
						<div className="flex mt-3 items-center w-full">
							<input
								type="text"
								placeholder="Name"
								value={firstName}
								autoComplete="off"
								className="px-3 space-x-[6px] py-3 sm:py-3 rounded-lg text-base font-normal font-inter leading-normal w-full placeholder-placeholder border border-gray-300 focus:border-blue-500 focus:outline-none"
								onChange={e => {
									setFirstName(e.target.value);
								}}
								maxLength={50}
							/>
						</div>
						<div className="text-textPrimary text-base sm:text-mdl font-normal font-inter pt-4 sm:pt-6">
							Referral Code (If Any)
						</div>
						<div className="mt-3 flex items-center w-full">
							<input
								type="text"
								placeholder="Code"
								autoComplete="off"
								value={referalCode}
								className="rounded-lg px-3 space-x-[6px] py-3 sm:py-3 text-base font-normal font-inter leading-normal w-full border border-gray-300 focus:border-blue-500  focus:outline-none placeholder-placeholder"
								onChange={e => {
									setReferalCode(e.target.value);
								}}
								maxLength={50}
							/>
						</div>
					</div>
				);
			default:
				return '';
		}
	};

	const onSubmit = () => {
		switch (step) {
			case 1:
				return (
					<Button
						onClick={() => {
							getLogin('username');
						}}
						disabled={
							loader ||
							(mobileNumber?.charAt(0) === '0' ? mobileNumber?.length <= 10 : mobileNumber?.length < 10) ||
							!ctoken
						}
						type="primary"
						title="Get OTP"
						className="w-full py-1 mt-6 md:mt-6 h-12 md:h-14 gap-1 inline-flex justify-center items-center 
             disabled:opacity-50 disabled:cursor-not-allowed"
						style={{ backgroundColor: websiteColor }}
					>
						{loader ? <Spinner /> : <RightArrow />}
					</Button>
				);

			case 2:
				return (
					<div className="m-auto w-full mdl:w-full">
						<Button
							onClick={() => getLogin('otp')}
							disabled={otp.length !== otpLength || loader}
							className="w-full py-1 mt-6 md:mt-6 h-12 md:h-14 gap-1 inline-flex justify-center items-center 
             disabled:opacity-50 disabled:cursor-not-allowed"
							style={{ backgroundColor: websiteColor }}
						>
							{loader ? (
								<Spinner />
							) : (
								<>
									<VerifyButton />
									<div className="text-white">Verify</div>
								</>
							)}
						</Button>
					</div>
				);

			case 3:
				return (
					<Button
						onClick={() => getLogin('details')}
						disabled={loader || !firstName.length}
						className="w-full py-1 mt-4 md:mt-6 h-12 md:h-14 gap-1 inline-flex justify-center items-center 
             disabled:opacity-50 disabled:cursor-not-allowed"
						style={{ backgroundColor: websiteColor }}
					>
						{loader ? <Spinner /> : <div className="text-white">Submit</div>}
					</Button>
				);

			default:
				return '';
		}
	};

	// ----------------- Render -----------------

	return (
		<Modal
			isOpen={visible}
			onRequestClose={onCloseButton}
			overlayClassName="fixed inset-0 bg-black bg-opacity-50 z-50"
			className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
        bg-white border-none focus:outline-none 
        ${isSmall ? 'h-full w-full px-10' : 'w-[95%] sm:w-[490px] h-auto lg:h-[630px] max-h-[90vh] rounded-2xl'} 
        flex items-center justify-center`}
			ariaHideApp={false}
			aria-modal="true"
		>
			<div
				onClick={e => e.stopPropagation()}
				className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
      bg-white 
      ${isSmall ? 'h-full w-full px-10' : 'w-[95%] sm:w-[490px] h-auto lg:h-[630px] max-h-[90vh] rounded-2xl'}
      flex items-center justify-center`}
			>
				{showCloseIcon && (
					<Button
						type="button"
						aria-label="Close login modal"
						className="z-50 absolute top-3 right-4 text-gray-400 bg-transparent rounded-lg text-sm w-6 h-6 sm:w-8 sm:h-8 ms-auto inline-flex justify-center items-center"
						onClick={onCloseButton}
					>
						<img src={crossIcon} alt="Close" />
					</Button>
				)}

				<div className="relative w-full mx-4 sm:mx-16">
					<img
						loading="lazy"
						src={websiteType === eWebsiteTypes.DINEORDER ? DineOrderLogo : kuickLogo}
						className="flex mx-auto w-[145px] sm:w-[175px] my-4 sm:my-6"
						alt="Logo"
					/>
					<div className={`w-full sm:mt-auto ${step > 1 ? 'sm:px-2' : 'sm:px-24'}`}>
						<div className="text-[20px] sm:text-[24px] font-bold leading-[38px] font-inter">{getHeader()}</div>
						<div
							className={`text-neutral-900 text-[14px] sm:text-md mdl:text-mdl font-normal font-inter leading-normal ${
								step === 2 || step === 3 ? 'pt-0' : 'pt-4'
							}`}
						>
							{getSubHeader()}
						</div>

						{getBody()}

						{/* ✅ reCAPTCHA box (step 1 only) */}
						{step === 1 && (
							<div
								className="flex justify-center origin-top transition-transform duration-300 ease-out mt-4"
								style={{
									width: '100%',
									maxWidth: '304px',
									transform: `scale(${recaptchaScale})`,
									transformOrigin: 'center'
								}}
							>
								<CaptchaBox visible={visible && step === 1} onToken={handleCaptchaToken} />
							</div>
						)}

						{/* Resend / Timer */}
						{step === 2 &&
							(showResend ? (
								<div className="flex justify-center pt-[23px] mdl:w-full m-auto">
									<div
										className="cursor-pointer w-[131px] sm:w-[156px] h-11 px-4 py-1 bg-pink-50 rounded-lg justify-center items-center gap-1 inline-flex"
										onClick={() => {
											getLogin('reotp', 'Yes');
										}}
									>
										<div className="text-center text-slate-500 text-base font-semibold font-inter leading-normal">
											Resend OTP
										</div>
									</div>
								</div>
							) : (
								<div className="text-center mt-6 md:mt-6">
									<span className="text-p6 font-bold">{timer}</span>
								</div>
							))}

						{/* Submit / Next button */}
						{onSubmit()}

						<div className="flex space-x-1 justify-center mt-3 sm:my-6">
							<div className="text-gray-500 text-base font-semibold font-inter leading-normal">
								{isLoginPage ? 'Don’t have an account?' : 'Already have an account?'}
							</div>
							<div
								onClick={handleToggle}
								className="text-blue-700 text-base font-semibold font-inter leading-normal cursor-pointer"
							>
								{isLoginPage ? 'Sign Up' : 'Login'}
							</div>
						</div>
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default LoginModal;
